import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BreadcrumbItems, Breadcrumbs, BreadcrumbValues } from "@emisgroup/cohort-builder-kit";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../reducer/CombineReducer";
import { EXECUTE } from "./Constant";
import { BreadcrumbValue, BreadcrumbValueOptions } from "./redux/Types";
import { BreadcrumbsItemsSlice } from "./index";

interface BreadcrumbsContainerTypes{
  /**
   * Breadcrumbs class name
   */
  className?: string;
  /**
   * Report name
   */
  reportName: string;
  /**
   * Active Tab Details
   */
  activeTab: string;
}

/**
 * Capitalizes the first letter
 * @param string String
 * @returns String with first letter capital
 */
const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Responsible for rendering the breadcrumbs
 * @returns tab breadcrumbs elements
 */
export const BreadcrumbsContainer: React.FC<BreadcrumbsContainerTypes> = (props: BreadcrumbsContainerTypes) => {
  const { className, reportName, activeTab } = props;
  const breadcrumbsItems = useSelector((state: IRootState) => state.breadcrumbsItems);
  const initialItem: BreadcrumbItems[] = [{
    name: reportName,
  }];
  const [baseItems, setBaseItems] = useState<BreadcrumbItems[]>(initialItem);
  const [items, setItems] = useState<BreadcrumbItems[]>(initialItem);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const breadcrumbsList: BreadcrumbValues[] = [
    BreadcrumbValueOptions.REPORTS,
    BreadcrumbValueOptions.SUMMARY,
    BreadcrumbValueOptions.VIEWEXECUTION
  ];

  // Updates the store based on active tab. Can be also used for other tabs than execute
  useEffect(() => {
    if (activeTab) {
      const newItems = [...initialItem];
      newItems.push({
        name: capitalizeFirstLetter(activeTab === EXECUTE ? translate("Breadcrumbs.Execution") : activeTab),
        onClick: () => {
          dispatch(BreadcrumbsItemsSlice.actions.updateItemView(undefined));
          dispatch(BreadcrumbsItemsSlice.actions.setBreadcrumbItem(undefined));
          if(activeTab === EXECUTE){
            dispatch(BreadcrumbsItemsSlice.actions.updateItemView(BreadcrumbValueOptions?.EXECUTION));
          }
        }
      });

      // We are using automatic batching as per the React18.
      // To instantly update the states, wrap with some promise.
      setTimeout(() => {
        setBaseItems(newItems);
        setItems(newItems);
      });
    }
  }, [activeTab, reportName]);

  const handleBreadcrumbClick = (itemValue: BreadcrumbValue): void => {
    dispatch(BreadcrumbsItemsSlice.actions.updateItemView(undefined));
    dispatch(BreadcrumbsItemsSlice.actions.setBreadcrumbItem(undefined));
    dispatch(BreadcrumbsItemsSlice.actions.updateItemView(itemValue));
  };

  // Updates the store based on clicked item
  useEffect(() => {
    const newItems = [...baseItems];

    if (breadcrumbsItems?.clickedItem !== undefined &&
      breadcrumbsList.includes(breadcrumbsItems?.clickedItem)) {
      newItems.push({
        name: translate("Breadcrumbs.Reports"),
        onClick: () => handleBreadcrumbClick(BreadcrumbValueOptions.REPORTS),
      });
    }

    if (breadcrumbsItems?.clickedItem !== undefined &&
      breadcrumbsItems?.clickedItem === BreadcrumbValueOptions.VIEWREPORT) {
      newItems.push(
        {
          name: translate("Breadcrumbs.Reports"),
          onClick: () => handleBreadcrumbClick(BreadcrumbValueOptions.REPORTS),
        },
        {
          name: translate("Breadcrumbs.Results"),
        }
      );
    }

    if (breadcrumbsItems?.breadcrumbSelectedItem === BreadcrumbValueOptions.REPORTS) {
      newItems.push({
        name: translate("Breadcrumbs.Reports"),
        onClick: () => handleBreadcrumbClick(BreadcrumbValueOptions.REPORTS),
      });
    }

    setItems(newItems);
  }, [breadcrumbsItems]);

  return (
    <div className={className}>
      <Breadcrumbs items={items} />
    </div>
  );
};
